/**
 * FavoriteRowToggle — inline star button next to a single field input.
 *
 * Use this in row patterns (multiple awards, exhibitions, publications)
 * where each row has its own current value to save/unsave as a favorite.
 * For single-input fields (e.g. Location), the FavoritesRail's built-in
 * star is sufficient and this component is unnecessary.
 */

import React from 'react';
import { IconButton, Tooltip } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { FieldFavoriteType } from '../utils/fieldFavoritesApi';
import { useFieldFavorites } from '../hooks/useFieldFavorites';

interface FavoriteRowToggleProps {
  type: FieldFavoriteType;
  value: string;
  ariaLabel?: string;
}

export const FavoriteRowToggle: React.FC<FavoriteRowToggleProps> = ({ type, value, ariaLabel }) => {
  const ctx = useFieldFavorites();
  // Render nothing when no provider is mounted (e.g. isolated component tests).
  if (!ctx) return null;
  const { isFavorite, addFavorite, removeFavorite } = ctx;
  const trimmed = value.trim();
  const saved = trimmed.length > 0 && isFavorite(type, trimmed);

  const handleClick = () => {
    if (!trimmed) return;
    if (saved) {
      void removeFavorite(type, trimmed);
    } else {
      void addFavorite(type, trimmed);
    }
  };

  const label = saved ? 'Unsave from favorites' : 'Save current as favorite';

  return (
    <Tooltip title={label}>
      <span>
        <IconButton
          size="small"
          onClick={handleClick}
          disabled={!trimmed}
          color={saved ? 'warning' : 'default'}
          aria-label={ariaLabel ?? label}
        >
          {saved ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
        </IconButton>
      </span>
    </Tooltip>
  );
};
